import { memo, useEffect } from "react";
import { Themes, ThemeContext, ToastContainer, Slide } from "@clintonelec/react-storybook";
import TitleBar from "Components/TitleBar";
import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import LoginPage from "Components/Auth/LoginPage";
import ForgotPasswordPage from "Components/Auth/ForgotPasswordPage";
import VerifyEmailPage from "Components/Auth/VerifyEmailPage";
import RegisterPage from "Components/Auth/RegisterPage";
import { toggleFeatureRenderer as toggleFeature } from "Components/Features";
import { Features } from "Data/Utils/Features";
import { useAppSelector } from "Data/Redux/Store";
import { selectToken, selectUser } from "Data/Redux/Slices/User";
import ReactGA from "react-ga4";
import { isElevatedUser } from "Data/Utils/User";
import SupportDashboard from "Components/Dashboards/SupportDashboard";
import UserDashboard from "Components/Dashboards/UserDashboard";

const App = () => {
	const { pathname } = useLocation();
	const user = useAppSelector(selectUser);
	const token = useAppSelector(selectToken);

	useEffect(() => {
		ReactGA.send({ hitType: "pageview", page: pathname });
	}, [ pathname ]);

	const renderBaseLayout = () => {
		if (!(user && token)) {
			return <Navigate to="/login" />;
		}

		return (
			<div className="content-area">
				<TitleBar />
				<Outlet />
			</div>
		);
	};

	const renderRegisterRoute = () => {
		if (!toggleFeature(Features.REGISTER, true, false)) {
			return;
		}

		return (
			<Route path="/register" element={ <RegisterPage /> } />
		);
	};

	const renderDashboard = () => {
		if (!user) {
			return;
		}

		if (isElevatedUser(user)) {
			return <SupportDashboard />;
		}

		return <UserDashboard />;
	};

	const compositeClass = `app-window app-window-${ Themes.LIGHT }`;

	return (
		<ThemeContext.Provider value={ Themes.LIGHT }>
			<div className={ compositeClass }>
				<Routes>
					<Route path="/login" element={ <LoginPage /> } />
					{ renderRegisterRoute() }
					<Route path="/reset-password/:token" element={ <ForgotPasswordPage /> } />
					<Route path="/verify-email/:id/:emailHash" element={ <VerifyEmailPage /> } />
					<Route path="/" element={ renderBaseLayout() }>
						<Route index element={ renderDashboard() } />
					</Route>
					<Route path="*" element={ <Navigate to="/" /> } />
				</Routes>
			</div>
			<ToastContainer
				position="top-right"
				autoClose={ 4500 }
				hideProgressBar
				newestOnTop={ false }
				closeOnClick
				rtl={ false }
				pauseOnFocusLoss
				draggable
				pauseOnHover
				transition={ Slide }
				className="cec-toast"
				limit={ 10 }
				style={ { width: 350 } }
				theme={ Themes.LIGHT }
			/>
		</ThemeContext.Provider>
	);
};

export default memo(App);
